import { IVector3 } from '../api.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { assert } from '../babylon/utility/Assert.js';
import { lerp_float } from '../Math.js';
import { PrimitiveBuilder } from './PrimitiveBuilder/PrimitiveBuilder.js';
const p1 = new BABYLON_Vector3(1, 1, 1);
const p2 = new BABYLON_Vector3(1, 1, -1.0);
const p3 = new BABYLON_Vector3(-1, 1, -1.0);
const p4 = new BABYLON_Vector3(-1, 1, 1.0);
const p5 = new BABYLON_Vector3(1, -1, 1.0);
const p6 = new BABYLON_Vector3(1, -1, -1.0);
const p7 = new BABYLON_Vector3(-1, -1, -1.0);
const p8 = new BABYLON_Vector3(-1, -1, 1.0);
const edges = [];
// The order of the verts is important because of backface culling
const edge1 = { p1: p1, p2: p2 };
const edge2 = { p1: p2, p2: p3 };
const edge3 = { p1: p3, p2: p4 };
const edge4 = { p1: p1, p2: p4 };
edges.push(edge1);
edges.push(edge2);
edges.push(edge3);
edges.push(edge4);
const edge5 = { p1: p5, p2: p6 };
const edge6 = { p1: p6, p2: p7 };
const edge7 = { p1: p7, p2: p8 };
const edge8 = { p1: p5, p2: p8 };
edges.push(edge5);
edges.push(edge6);
edges.push(edge7);
edges.push(edge8);
const edge9 = { p1: p1, p2: p5 };
const edge10 = { p1: p2, p2: p6 };
const edge11 = { p1: p3, p2: p7 };
const edge12 = { p1: p4, p2: p8 };
edges.push(edge9);
edges.push(edge10);
edges.push(edge11);
edges.push(edge12);
function color(r = 1, g = 1, b = 1) {
    return { r: r, g: g, b: b };
}
/*
 * A class that creates a transparent box with specified size and position that will be visible once a url is choosen to be loaded, and which will then fade out and disappear (both visually and from the scene graph)
 */
export class ProxyLoader {
    create_preview_material() {
        const viewer = this._viewer;
        const id = viewer.create_material('ProxyLoader_Material');
        viewer.set_base_color(id, color(0.5, 0.5, 0.5));
        viewer.set_roughness(id, 0.7);
        viewer.set_metallic(id, 0.0);
        viewer.set_opacity(id, ProxyLoader._base_opacity);
        return id;
    }
    constructor(viewer, url, size, center, model_transform) {
        this._preview_material = null;
        this._started_loading = false;
        this._disposed = false;
        this.animation_complete = new Promise((resolve, reject) => {
            this._loading_resolve = resolve;
            this._loading_reject = reject;
        });
        this._object_loading_id = null;
        this._viewer = viewer;
        this._size = size;
        this._url = url;
        this._model_transform = model_transform !== null && model_transform !== void 0 ? model_transform : null;
        if (ProxyLoader._shared_preview_material === null) {
            ProxyLoader._shared_preview_material = this.create_preview_material();
            viewer.get_viewer_dispose_observable().add_once(() => {
                ProxyLoader._shared_preview_material = null;
            });
        }
        if (ProxyLoader._shared_preview_outline_material === null) {
            ProxyLoader._shared_preview_outline_material = viewer.create_material('ProxyLoader_Material');
            viewer.set_unlit(ProxyLoader._shared_preview_outline_material, true);
            viewer.get_viewer_dispose_observable().add_once(() => {
                ProxyLoader._shared_preview_outline_material = null;
            });
        }
        const advanced_viewer = viewer;
        // Create a box with the provided size and parent it to the model that is loading, hide it until the model is being loaded though
        const primitive_builder = new PrimitiveBuilder(viewer);
        this._preview_box = primitive_builder.create_box(size.x, size.y, size.z, { material: ProxyLoader._shared_preview_material });
        this._viewer.set_position(this._preview_box, center.x, center.y, center.z);
        advanced_viewer.set_enabled(this._preview_box, false);
        // Create the outline lines
        const outline_positions = this.create_outline_positions(new BABYLON_Vector3(center.x, center.y, center.z));
        const outline_indices = new Uint16Array(outline_positions.length / 3);
        for (let i = 0; i < outline_indices.length; i++) {
            outline_indices[i] = i;
        }
        this._preview_outline = this._viewer.create_custom_mesh({
            positions: outline_positions,
            indices: outline_indices
        }, ProxyLoader._shared_preview_outline_material);
        advanced_viewer.set_enabled(this._preview_outline, false);
    }
    dispose() {
        this._disposed = true;
        // There was a bug where Core removed all children including preview box before this callback was called
        // Check if object still exists before removing it
        if (this._viewer.object_exists(this._preview_box)) {
            this._viewer.delete_object(this._preview_box); // this will not remove material assigned to object
        }
        // Same as above
        if (this._viewer.object_exists(this._preview_outline)) {
            this._viewer.delete_object(this._preview_outline); // this will not remove material assigned to object
        }
        // Cleanup material
        if (this._preview_material !== null) {
            this._viewer.delete_material(this._preview_material);
        }
    }
    *object_is_loading(reject) {
        while (!this._disposed) {
            if (!this._viewer.object_exists(this._object_loading_id)) {
                reject(`object id ${this._object_loading_id} deleted before loaded`);
            }
            yield undefined;
        }
        return undefined;
    }
    *animate_opacity(on_complete) {
        let elapsed_time = 0;
        if (this._disposed)
            return undefined;
        if (this._preview_material === null) {
            // Create a unique material for us when we are starting the animation
            this._preview_material = this.create_preview_material();
            this._viewer.set_material(this._preview_box, this._preview_material);
        }
        while (elapsed_time < ProxyLoader._animation_duration) {
            // yield here receives DeltaTime object with information for this frame
            const delta = yield;
            if (this._disposed)
                return undefined; // We don't do anything after a dispose so we might as well just return here
            elapsed_time += delta.seconds;
            const opacity = lerp_float(ProxyLoader._base_opacity, 0, elapsed_time / ProxyLoader._animation_duration);
            this._viewer.set_opacity(this._preview_material, opacity);
        }
        if (!this._disposed) {
            this._viewer.set_opacity(this._preview_material, 0);
            on_complete();
        }
        return undefined;
    }
    create_outline_positions(center) {
        const triangle_floats = [];
        const resolution = 4;
        const border_relative_size = 0.0025;
        const radius = border_relative_size *
            new BABYLON_Vector3(this._size.x, this._size.y, this._size.z).length();
        const normal_scales = new Array(resolution)
            .fill(undefined)
            .map((v, i) => Math.cos(Math.PI * 0.25 + 2.0 * Math.PI * (i / resolution)));
        const binormal_scales = new Array(resolution)
            .fill(undefined)
            .map((v, i) => Math.sin(Math.PI * 0.25 + 2.0 * Math.PI * (i / resolution)));
        const half_size = new BABYLON_Vector3(this._size.x * 0.5, this._size.y * 0.5, this._size.z * 0.5);
        edges.forEach((edge) => {
            const p1 = center.add(edge.p1.multiply(half_size));
            const p2 = center.add(edge.p2.multiply(half_size));
            const p1p2 = p1.subtract(p2).normalize();
            const perp = IVector3.perpendicular(p1p2);
            const normal = new BABYLON_Vector3(perp.x, perp.y, perp.z);
            const binormal = p1p2.cross(normal);
            const edge_shift = Math.cos(Math.PI / 4.0) * radius;
            p1.addInPlace(p1p2.scale(edge_shift));
            p2.subtractInPlace(p1p2.scale(edge_shift));
            for (let i = 0; i < resolution; i++) {
                const offset_vector1 = normal
                    .scale(normal_scales[i])
                    .addInPlace(binormal.scale(binormal_scales[i]))
                    .scaleInPlace(radius);
                const cylinder_point1 = p1.add(offset_vector1);
                const cylinder_point2 = p2.add(offset_vector1);
                const offset_vector2 = normal
                    .scale(normal_scales[(i + 1) % resolution])
                    .addInPlace(binormal.scale(binormal_scales[(i + 1) % resolution]))
                    .scaleInPlace(radius);
                const cylinder_point3 = p1.add(offset_vector2);
                const cylinder_point4 = p2.add(offset_vector2);
                // Add our quads. Note that we let the viewer triangulate into triangles.
                const points = [
                    cylinder_point1,
                    cylinder_point2,
                    cylinder_point4,
                    cylinder_point1,
                    cylinder_point4,
                    cylinder_point3
                ];
                // cylinder_point
                points.forEach((point) => {
                    triangle_floats.push(point.x);
                    triangle_floats.push(point.y);
                    triangle_floats.push(point.z);
                });
            }
        });
        return new Float32Array(triangle_floats);
    }
    /**
     * Starts to load the object and sets the loading box to visible, once the object has loaded the loading box will fade out and then be removed from the scene
     */
    load_model_with_box_proxy() {
        if (this._started_loading) {
            throw new Error('Model has already started to load');
        }
        // Load model
        const id = this._viewer.load_model(this._url, undefined, this._model_transform === null ? undefined : this._model_transform);
        this._object_loading_id = id;
        // Make the box enabled
        this._viewer.set_parent(this._preview_box, this._object_loading_id);
        this._viewer.set_parent(this._preview_outline, this._object_loading_id);
        const advanced_viewer = this._viewer;
        advanced_viewer.set_enabled(this._preview_box, true);
        advanced_viewer.set_enabled(this._preview_outline, true);
        let animation_resolve = null;
        let animation_reject = null;
        const internal_animation_promise = new Promise((resolve, reject) => {
            animation_resolve = resolve;
            animation_reject = reject;
        });
        assert(animation_resolve !== null, 'ProxyLoader failed to initialize animation promise chain');
        assert(animation_reject !== null, 'ProxyLoader failed to initialize animation promise chain');
        // When the model is ready we can start the opacity animation which when ended removes the box
        this._viewer.start_coroutine(this.object_is_loading(animation_reject)); // Start the function that checks if to reject the animation if the object the animation is for is deleted
        this._viewer.object_ready(id).then(() => {
            this._viewer.start_coroutine(this.animate_opacity(animation_resolve)); // Start the animate opacity loop
        }).catch((err) => {
            this._loading_reject(err);
        });
        internal_animation_promise.then(() => {
            this.dispose();
            this._loading_resolve();
        }, (err) => {
            this.dispose();
            this._loading_reject(err);
        });
        // Catch the error so we don't have uncaught in promise if user doesn't catch it
        this.animation_complete.catch(() => { });
        this._started_loading = true;
        return this._object_loading_id;
    }
}
ProxyLoader._animation_duration = 0.3; // default to 0.3 sec
ProxyLoader._base_opacity = 0.75;
ProxyLoader._shared_preview_material = null;
ProxyLoader._shared_preview_outline_material = null;
//# sourceMappingURL=ProxyLoader.js.map